import * as React from "react";
import { Link as RouterLink } from "react-router-dom";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import orange from "@material-ui/core/colors/orange";

import { PageWrapper } from "../components/PageWrapper/PageWrapper";
import { Container } from "../components/PageWrapper/Container";
import { ColoredBandContainer } from "../components/PageWrapper/ColoredBandContainer";
import { PageHeader } from "../components/PageWrapper/PageHeader";
import { SignupButton } from "../components/SignupButton/SignupButton";
import { Iris, IrisAssessment } from "../components/Iris";
import { Pricing } from "./PricingPage";
import { ActionableInsights } from "./AuthorPage";

import sample_report from "../images/sample_report.png";
import author from "../images/author.svg";
import publisher from "../images/publisher.svg";
import agent from "../images/agent.svg";

export const IrisAssessmentPage = () => {
  return (
    <PageWrapper>
      <Container>
        <Hero />
        <Benefits />
      </Container>
      <ColoredBandContainer>
        <ActionableInsights />
      </ColoredBandContainer>
      <ColoredBandContainer dark={true} noGutter={true}>
        <GetStarted darkMode={true} />
      </ColoredBandContainer>
      <Container>
        <Box mt={8} mb={16}>
          <PageHeader
            overline="Pricing"
            title="Flexible and affordable"
            subtitle={
              <>
                <Iris /> is ~30x more affordable than traditional manuscript
                assessments.
              </>
            }
          />
          <Pricing canExpand={true} mainPrice="singleReport" />
        </Box>
      </Container>
    </PageWrapper>
  );
};

const Hero = () => {
  return (
    <Grid
      container={true}
      spacing={4}
      style={{ marginTop: 16, marginBottom: 32 }}
    >
      <Grid item={true} xs={12} md={6}>
        <Box mt={4} mb={2}>
          <Typography
            component="span"
            variant="h2"
            color="primary"
            variantMapping={{ h2: "h1" }}
            style={{
              background: `linear-gradient(180deg, transparent 82%, ${orange[100]} 0%)`,
            }}
          >
            Affordable, objective{" "}
          </Typography>
          <Typography
            component="span"
            variant="h2"
            color="textPrimary"
            variantMapping={{ h2: "h1" }}
            style={{ fontWeight: 600 }}
          >
            manuscript analysis
          </Typography>
        </Box>
        <Box mb={4}>
          <Typography variant="h6" component="p" color="textSecondary">
            Upload your manuscript and get actionable feedback for your entire
            work across 30+ metrics within minutes.
          </Typography>
        </Box>
        <Box>
          <SignupButton textAlign="left" place="iris-assessment" />
        </Box>
      </Grid>
      <Grid item={true} xs={12} md={6}>
        <Box boxShadow={3} borderRadius={8}>
          <img
            src={sample_report}
            style={{
              width: "100%",
              maxWidth: 800,
            }}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

const Benefits = () => {
  return (
    <Box mt={6} mb={2}>
      <PageHeader overline="Serving the Industry" />
      <Box>
        <Grid container={true} spacing={4} alignItems="stretch">
          <Grid item={true} xs={12} md={4} style={{ display: "flex" }}>
            <Card variant="outlined" style={{ display: "flex" }}>
              <Box display="flex" flexDirection="column" m={4}>
                <Box flexGrow={1}>
                  <Box
                    mb={4}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height={200}
                  >
                    <img src={author} height="100%" />
                  </Box>
                  <Typography
                    variant="h6"
                    gutterBottom={true}
                    style={{ fontWeight: 700 }}
                  >
                    For Authors
                  </Typography>
                  <Typography color="textSecondary">
                    Fiction is about both; the story and telling it right.
                    Authors can use detailed metrics to compare their work with
                    benchmarks from the market, find underperforming sections
                    and improve their writing.
                  </Typography>
                </Box>
                <Box
                  flexGrow={0}
                  display="flex"
                  justifyContent="flex-end"
                  mt={4}
                >
                  <Button component={RouterLink} to="/authors" color="primary">
                    Learn more
                  </Button>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item={true} xs={12} md={4} style={{ display: "flex" }}>
            <Card variant="outlined" style={{ display: "flex" }}>
              <Box display="flex" flexDirection="column" m={4}>
                <Box flexGrow={1}>
                  <Box
                    mb={4}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height={200}
                  >
                    <img
                      src={publisher}
                      height="100%"
                      style={{ maxWidth: 260 }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    gutterBottom={true}
                    style={{ fontWeight: 700 }}
                  >
                    For Publishers
                  </Typography>
                  <Typography color="textSecondary">
                    Finding the next best seller is like finding a needle in a
                    haystack... but what if we give you a metal detector?
                    Optimise your slush pile by using <Iris /> to assess, filter
                    and rank your incoming manuscripts!
                  </Typography>
                </Box>
                <Box
                  flexGrow={0}
                  display="flex"
                  justifyContent="flex-end"
                  mt={4}
                >
                  <Button
                    component={RouterLink}
                    to="/publishers"
                    color="primary"
                  >
                    Learn more
                  </Button>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item={true} xs={12} md={4} style={{ display: "flex" }}>
            <Card variant="outlined" style={{ display: "flex" }}>
              <Box display="flex" flexDirection="column" m={4}>
                <Box flexGrow={1}>
                  <Box
                    mb={4}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height={200}
                  >
                    <img src={agent} height="100%" />
                  </Box>
                  <Typography
                    variant="h6"
                    gutterBottom={true}
                    style={{ fontWeight: 700 }}
                  >
                    For Agents & Editors
                  </Typography>
                  <Typography color="textSecondary">
                    Collaborate closely with authors to continuously improve
                    their manuscripts using our <Iris /> technology.
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

const GetStarted = (props: { darkMode: boolean }) => {
  const { darkMode } = props;

  return (
    <Box my={2}>
      <PageHeader
        overline="Get Started"
        title={
          <>
            Get started with <IrisAssessment /> today
          </>
        }
        subtitle={
          <>
            Whether you're drafting your first novel or polishing your next
            bestseller, it's your secret weapon to write better fiction faster.
          </>
        }
        align="center"
        darkMode={darkMode}
      />
      <SignupButton place="iris-assessment" />
    </Box>
  );
};

export default IrisAssessmentPage;



// WEBPACK FOOTER //
// ./src/pages/IrisAssessmentPage.tsx